"use client"

import { useRef, useState, useEffect, useCallback } from "react"
import { StoreBanner } from "../../types/banner"
import BannerSlide from "../banner-slide"

type BannerCarouselProps = {
  banners: StoreBanner[]
}

export default function BannerCarousel({ banners }: BannerCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const scrollToIndex = useCallback((index: number) => {
    if (!scrollRef.current) return
    const slideWidth = scrollRef.current.offsetWidth
    scrollRef.current.scrollTo({ left: slideWidth * index, behavior: "smooth" })
  }, [])

  // Track active slide via IntersectionObserver
  useEffect(() => {
    const container = scrollRef.current
    if (!container || banners.length <= 1) return

    const slides = Array.from(container.children) as HTMLElement[]
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = slides.indexOf(entry.target as HTMLElement)
            if (idx !== -1) setActiveIndex(idx)
          }
        }
      },
      { root: container, threshold: 0.5 }
    )

    slides.forEach((slide) => observer.observe(slide))
    return () => observer.disconnect()
  }, [banners.length])

  // Auto-advance
  useEffect(() => {
    if (banners.length <= 1 || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current)
      return
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % banners.length
        scrollToIndex(next)
        return next
      })
    }, 5000)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [banners.length, isPaused, scrollToIndex])

  // Single banner — no carousel
  if (banners.length === 1) {
    return (
      <section aria-label="Homepage banner">
        <BannerSlide banner={banners[0]} isFirst />
      </section>
    )
  }

  return (
    <section
      role="region"
      aria-label="Homepage banners"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      {/* Scroll container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {banners.map((banner, i) => (
          <BannerSlide key={banner.id} banner={banner} isFirst={i === 0} />
        ))}
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 py-3" role="tablist" aria-label="Banner navigation">
        {banners.map((banner, i) => (
          <button
            key={banner.id}
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={`Go to banner ${i + 1}`}
            onClick={() => scrollToIndex(i)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              i === activeIndex
                ? "w-8 bg-brand-primary"
                : "w-2.5 bg-gray-300 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>
    </section>
  )
}
